/**
 * Minimal IndexedDB helpers — the boilerplate, in one place.
 *
 * WHY THIS EXISTS NOW AND NOT IN INCREMENT 2. `pending-ops.ts` grew its own copy of the
 * open/await dance because it was the only consumer. The read cache is the second, and two
 * copies of transaction handling is exactly where a subtle difference hides.
 *
 * `pending-ops.ts` IS DELIBERATELY NOT MIGRATED TO THIS in the same change. It holds work
 * a user has captured and not yet handed over, its IndexedDB path is covered only by
 * Playwright (jsdom has no IndexedDB), and refactoring that file alongside shipping a new
 * feature is how you find out later which of the two broke it. Unifying it is a commit
 * that should do nothing else.
 */

export function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB request failed'));
    };
  });
}

/**
 * Await the TRANSACTION's completion, not the request's success.
 *
 * A request can succeed inside a transaction that then aborts. Resolving on the request
 * would report a durable write that never landed — the silent failure this whole area
 * exists to remove.
 */
export function idbTransaction(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      resolve();
    };
    tx.onerror = () => {
      reject(tx.error ?? new Error('IndexedDB transaction failed'));
    };
    tx.onabort = () => {
      reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    };
  });
}

/** Opened lazily and once per database — an open per operation would serialise every
 * write behind a fresh handshake, and a version change blocks while a connection is up. */
export function idbOpener(
  dbName: string,
  version: number,
  upgrade: (db: IDBDatabase) => void,
): () => Promise<IDBDatabase> {
  let connection: Promise<IDBDatabase> | undefined;
  return () => {
    connection ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(dbName, version);
      request.onupgradeneeded = () => {
        upgrade(request.result);
      };
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        reject(request.error ?? new Error(`could not open ${dbName}`));
      };
      // Firefox in private mode and Safari with storage disabled reject rather than
      // throwing synchronously. Callers turn this into a visible refusal or a miss;
      // it must never read as "there is nothing here".
      request.onblocked = () => {
        reject(new Error(`${dbName} is blocked by another tab`));
      };
    });
    return connection;
  };
}
